import { useContext, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PermissionMatrixContext, { cellKey } from "@/Contexts/PermissionMatrix/Context";
import ChangeDetectedPrompt from "../ChangeDetectedPrompt";
import NothingSelected from "./NothingSelected";
import RolesStrip from "./RolesStrip";
import CellScopeMenu from "./CellScopeMenu";
import PermissionContext from "@/Contexts/Permissions";
import { PermissionEntityEnum, PermissionActionEnum } from "@/graphql/generated/graphql";
import RequestPermissionTooltip from "@/components/RequestPermissionTooltip";
import ActionHeaderCell from "../ActionHeaderCell";

const normalize = (value: string) => value.trim().toLowerCase();

// Column order for the grid; entities only render the actions they support.
const ACTION_ORDER = [
  "view",
  "create",
  "edit",
  "manage",
  "export",
  "send_email",
  "approve",
  "publish",
  "review",
  "issue",
];

interface OpenCell {
  anchorEl: HTMLElement;
  action: string;
  entity: string;
  projectScoped: boolean;
}

const AccessContainer = () => {
  const {
    registry,
    registryLoading,
    selectedEmploymentId,
    selectedEmployee,
    selectedPermissionsLoading,
    roleGrantSources,
    draft,
    toggleCell,
    setColumn,
  } = useContext(PermissionMatrixContext);
  const { canPerformAction, checkRequestExists, requestPermission } = useContext(PermissionContext);
  const { Manage } = PermissionActionEnum;
  const { PermissionManagement} = PermissionEntityEnum;

  const [search, setSearch] = useState("");
  const [openCell, setOpenCell] = useState<OpenCell | null>(null);

  // Action columns actually used by at least one entity, in canonical order.
  const actionColumns = useMemo(() => {
    const present = new Map<string, { key: string; name: string; description?: string | null }>();
    (registry ?? []).forEach((entity) => {
      entity.actions.forEach((action) => {
        if (!present.has(action.key)) {
          present.set(action.key, { key: action.key, name: action.name, description: action.description });
        }
      });
    });
    return ACTION_ORDER.flatMap((key) => {
      const found = present.get(key);
      return found ? [found] : [];
    });
  }, [registry]);

  const visibleEntities = useMemo(() => {
    const query = normalize(search);
    const entities = registry ?? [];
    if (!query) return entities;
    return entities.filter((entity) => normalize(entity.name).includes(query));
  }, [registry, search]);

  if (!selectedEmploymentId) {
    return (
      <Box sx={{ flex: 1, height: "100%" }}>
        <NothingSelected />
      </Box>
    );
  }

  const loading = registryLoading || selectedPermissionsLoading;

  return (
    <Box sx={{ flex: 1, height: "100%", boxSizing: "border-box", overflow: "auto" }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          borderBottom: "1px solid rgba(0,0,0,0.08)",
        }}
      >
        <Avatar src={selectedEmployee?.imageUrl ?? undefined} sx={{ width: 32, height: 32 }}>
          {selectedEmployee?.fullName?.[0]}
        </Avatar>
        <Typography variant="subtitle1" fontWeight={800} sx={{ flex: 1 }}>
          {selectedEmployee?.fullName ?? "Selected user"}
        </Typography>
        <TextField
          size="small"
          placeholder="Search modules"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>

      <RolesStrip />

      {loading ? (
        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading permissions…
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ boxSizing: "border-box", border: "1px solid rgba(0,0,0,0.12)", height: "calc(100% - 150px)"  }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    minWidth: 260,
                    fontWeight: 800,
                    bgcolor: "#f7f7f7",
                    borderRight: "1px solid rgba(0,0,0,0.12)",
                  }}
                >
                  Module
                </TableCell>
                {actionColumns.map((action) => (
                  <ActionHeaderCell
                    key={action.key}
                    action={action}
                    entities={visibleEntities}
                    selectedKeys={draft}
                    readOnlySelectedKeys={roleGrantSources}
                    onChange={setColumn}
                  />
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {visibleEntities.map((entity) => {
                const supported = new Set(entity.actions.map((a) => a.key));

                return (
                  <TableRow key={entity.key} hover>
                    <TableCell sx={{ borderRight: "1px solid rgba(0,0,0,0.12)" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {entity.name}
                        </Typography>
                        {entity.projectScoped && (
                          <Tooltip title="Permissions on this module can be limited to specific projects" arrow>
                            <Chip label="per project" size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>

                    {actionColumns.map((action) => {
                      if (!supported.has(action.key)) {
                        return (
                          <TableCell key={action.key} align="center" sx={{ color: "text.disabled" }}>
                            —
                          </TableCell>
                        );
                      }

                      const key = cellKey(action.key, entity.key);
                      const fromRoles = roleGrantSources.get(key);
                      const cell = draft.get(key);
                      const granted = Boolean(cell);

                      if (fromRoles?.length) {
                        return (
                          <TableCell key={action.key} align="center">
                            <Tooltip title={`Granted by role: ${fromRoles.join(", ")}`} arrow>
                              <span>
                                <Checkbox checked disabled size="small" />
                              </span>
                            </Tooltip>
                          </TableCell>
                        );
                      }

                      if (!canPerformAction(Manage, PermissionManagement)) {
                        return (
                          <TableCell key={action.key} align="center">
                            <RequestPermissionTooltip
                              hasPermission={false}
                              requiredPermission={{
                                action: Manage,
                                entity: PermissionManagement
                              }}
                              checkRequestExists={checkRequestExists}
                              onRequest={requestPermission}
                            >
                              <Checkbox checked={granted} disabled size="small" />
                            </RequestPermissionTooltip>
                          </TableCell>
                        );
                      }

                      const scopedToProjects = cell?.projectIds != null;
                      const hasExpiry = Boolean(cell?.validTill);

                      return (
                        <TableCell key={action.key} align="center">
                          <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                            <Checkbox
                              size="small"
                              checked={granted}
                              onChange={() => toggleCell(action.key, entity.key)}
                            />
                            {granted && (
                              <Tooltip
                                title={
                                  scopedToProjects
                                    ? `Limited to ${cell?.projectIds?.length ?? 0} project(s)`
                                    : hasExpiry
                                      ? "Temporary permission"
                                      : "Company-wide and permanent — click to scope or set an expiry"
                                }
                                arrow
                              >
                                <IconButton
                                  size="small"
                                  onClick={(event) =>
                                    setOpenCell({
                                      anchorEl: event.currentTarget,
                                      action: action.key,
                                      entity: entity.key,
                                      projectScoped: entity.projectScoped,
                                    })
                                  }
                                >
                                  {hasExpiry ? (
                                    <ScheduleIcon fontSize="inherit" color="warning" />
                                  ) : (
                                    <TuneIcon fontSize="inherit" color={scopedToProjects ? "primary" : "disabled"} />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {openCell && (
        <CellScopeMenu
          anchorEl={openCell.anchorEl}
          action={openCell.action}
          entity={openCell.entity}
          projectScoped={openCell.projectScoped}
          onClose={() => setOpenCell(null)}
        />
      )}

      <ChangeDetectedPrompt />
    </Box>
  );
};

export default AccessContainer;
